/**
 * ScanContext — global, route-independent scan orchestration.
 *
 * Mounted once at the App root (above <Routes>) so that a scan started on one page
 * (e.g. the onboarding wizard, or the AI details page) keeps running — and keeps
 * reporting progress via the top-center notification bar — even if the user
 * navigates elsewhere. The backend job itself is always async (SQS/Batch or a
 * plain HTTP call); this context just makes the *frontend* aware of it regardless
 * of which page is mounted.
 *
 * Usage:
 *   const { runScan, isRunning } = useScan();
 *   runScan({ key: `ai:${appId}`, label: 'Valet Parking — Security Scan' }, async (setMessage) => {
 *     ...
 *     return { status: 'ok', message: 'Scan completed' };
 *   });
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';

export interface ScanMeta {
  /** Unique id for this scan — prevents duplicate concurrent runs for the same target (e.g. `ai:${appId}`). */
  key: string;
  /** Human-readable label shown in the notification bar. */
  label: string;
}

export interface ActiveScan {
  meta: ScanMeta;
  startedAt: number;
  elapsedMs: number;
  message: string;
}

export interface ScanToast {
  id: string;
  message: string;
  severity: 'success' | 'warning' | 'error';
}

export interface ScanTaskResult {
  status: 'ok' | 'partial' | 'error';
  message: string;
}

type ScanTask = (setMessage: (m: string) => void) => Promise<ScanTaskResult>;

interface ScanContextType {
  scans: ActiveScan[];
  toasts: ScanToast[];
  dismissToast: (id: string) => void;
  isRunning: (key: string) => boolean;
  /** Starts a scan if one isn't already running for this key. No-ops (and returns false) if already running. */
  runScan: (meta: ScanMeta, task: ScanTask) => boolean;
}

const ScanContext = createContext<ScanContextType | null>(null);

export const useScan = (): ScanContextType => {
  const ctx = useContext(ScanContext);
  if (!ctx) throw new Error('useScan must be used inside ScanProvider');
  return ctx;
};

export const ScanProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [scans, setScans] = useState<ActiveScan[]>([]);
  const [toasts, setToasts] = useState<ScanToast[]>([]);
  const runningKeys = useRef<Set<string>>(new Set());
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Elapsed-time ticker — only runs while at least one scan is active.
  useEffect(() => {
    if (scans.length === 0) {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      return;
    }
    if (tickRef.current) return;
    tickRef.current = setInterval(() => {
      setScans((prev) => prev.map((s) => ({ ...s, elapsedMs: Date.now() - s.startedAt })));
    }, 1000);
    return () => { if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; } };
  }, [scans.length]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback((message: string, severity: ScanToast['severity']) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts((prev) => [...prev, { id, message, severity }]);
    // Auto-dismiss after 8s — user can also dismiss manually.
    setTimeout(() => dismissToast(id), 8000);
  }, [dismissToast]);

  const isRunning = useCallback((key: string) => runningKeys.current.has(key), []);

  const runScan = useCallback((meta: ScanMeta, task: ScanTask): boolean => {
    if (runningKeys.current.has(meta.key)) return false;
    runningKeys.current.add(meta.key);

    const startedAt = Date.now();
    setScans((prev) => [...prev, { meta, startedAt, elapsedMs: 0, message: 'Starting…' }]);

    const setMessage = (message: string) => {
      setScans((prev) => prev.map((s) => (s.meta.key === meta.key ? { ...s, message } : s)));
    };

    (async () => {
      try {
        const result = await task(setMessage);
        pushToast(`${meta.label}: ${result.message}`, result.status === 'ok' ? 'success' : result.status === 'partial' ? 'warning' : 'error');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Scan failed.';
        pushToast(`${meta.label}: ${msg}`, 'error');
      } finally {
        runningKeys.current.delete(meta.key);
        setScans((prev) => prev.filter((s) => s.meta.key !== meta.key));
      }
    })();

    return true;
  }, [pushToast]);

  const value = useMemo<ScanContextType>(() => ({
    scans, toasts, dismissToast, isRunning, runScan,
  }), [scans, toasts, dismissToast, isRunning, runScan]);

  return <ScanContext.Provider value={value}>{children}</ScanContext.Provider>;
};
